import { useForm } from 'react-hook-form';
import { useState, useCallback } from 'react';

/**
 * Custom hook for managing survey form state and validation
 * 
 * This hook handles:
 * - React Hook Form integration
 * - Form data transformation for API submission
 * - Loading saved answers back into the form
 * - Form validation state management
 * 
 * @returns {Object} Form methods and state
 */
export const useSurveyForm = () => {
  // Initialize React Hook Form with default configuration
  const formMethods = useForm({
    // Keep default values when resetting (important for controlled components)
    shouldUnregister: false,
    // Validate on blur for better UX
    mode: 'onBlur',
    // Default values for form fields
    defaultValues: {},
  });

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    watch,
    reset,
    formState: { errors, isDirty },
  } = formMethods;

  // Track if the form has unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  /**
   * Transforms saved answer data back into form format
   * This converts database format back to the form field structure
   * 
   * @param {Array} savedAnswers - Array of saved answer objects from database
   * @param {Array} savedImages - Array of saved image objects from database
   * @returns {Object} Form values object ready for React Hook Form
   */
  const transformSavedDataToFormValues = useCallback((savedAnswers, savedImages) => {
    const formValues = {};
    const frIDs = {};

    // Process saved answers
    if (savedAnswers && savedAnswers.length > 0) {
      savedAnswers.forEach(({ formName, issue, owner, value, frID, fwtId }) => {
        const [section, area, id] = formName.split('-');
        
        // Handle different field types
        if (section === 'strengths') {
          formValues.strengths = value;
        } else if (section === 'opportunities') {
          formValues.opportunities = value;
        } else {
          // Regular form field
          formValues[formName] = String(value);
          
          // Store record ID for updates vs inserts
          if (frID) {
            frIDs[formName] = frID;
          }
          
          // Handle associated explanation and responsibility fields
          if (issue) {
            formValues[`${section}-${area}-explain`] = issue;
          }
          
          // Always set the responsible field, even if owner is empty/null
          // This ensures the form shows the correct value from database
          const responsibleFieldName = `${section}-${area}-responsible`;
          const ownerValue = owner || ''; // Convert null/undefined to empty string
          formValues[responsibleFieldName] = ownerValue;
          
        }
      });
    }

    // Process saved images
    if (savedImages && savedImages.length > 0) {
      // Group images by section-area
      const imageGroups = {};
      
      savedImages.forEach((imageData) => {
        const fileName = imageData.fileName || imageData.filename;
        const gcsUrl = imageData.gcsUrl;
        const originalName = imageData.originalName || fileName;
        const mimeType = imageData.mimeType || 'image/jpeg';
        const fileSize = imageData.fileSize || 0;
        
        const [section, area, id] = fileName.split('-');
        const key = `${section}-${area}`;

        if (!imageGroups[key]) {
          imageGroups[key] = [];
        }

        // Create a File-like object that represents the saved image
        // This includes metadata needed for display and upload detection
        const savedFile = new File(
          [new Blob([], { type: mimeType })], // Empty blob since we have the GCS URL
          fileName,
          { 
            type: mimeType,
            lastModified: imageData.uploadedAt ? new Date(imageData.uploadedAt).getTime() : Date.now()
          }
        );
        
        // Add custom properties to track this as a saved image
        savedFile._isSavedImage = true;
        savedFile._gcsUrl = gcsUrl;
        savedFile._originalName = originalName;
        savedFile._fileSize = fileSize;
        savedFile._storageType = imageData.storageType || 'gcs';
        savedFile._facilityRecordId = imageData.facilityRecordId || imageData.id;
        
        imageGroups[key].push(savedFile);
      });

      // Add image arrays to form values
      Object.keys(imageGroups).forEach((key) => {
        formValues[`${key}-files`] = imageGroups[key];
      });
    }

    return { formValues, frIDs };
  }, []);

  /**
   * Loads saved data into the form
   * This is called when user chooses to restore a saved survey
   * 
   * @param {Array} savedAnswers - Saved answers from database
   * @param {Array} savedImages - Saved images from database
   * @returns {Object} Form record IDs for tracking updates vs inserts
   */
  const loadSavedData = useCallback((savedAnswers, savedImages) => {
    const { formValues, frIDs } = transformSavedDataToFormValues(savedAnswers, savedImages);
    
    // Reset form with saved values while keeping default values
    reset(formValues, { keepDefaultValues: true });
    
    // Mark form as clean since we just loaded saved data
    setHasUnsavedChanges(false);
    
    return frIDs;
  }, [reset, transformSavedDataToFormValues]);

  /**
   * Merges saved survey data into the existing form without resetting other fields
   * 
   * @param {Array} savedAnswers - Array of saved answer objects from database
   * @param {Array} savedImages - Array of saved image objects from database
   * @returns {Object} Form record IDs for tracking updates vs inserts
   */
  const mergeSavedData = useCallback((savedAnswers, savedImages) => {
    const { formValues, frIDs } = transformSavedDataToFormValues(savedAnswers, savedImages);
    
    // Set individual field values instead of resetting the entire form
    Object.keys(formValues).forEach(fieldName => {
      setValue(fieldName, formValues[fieldName], { shouldDirty: false });
    });
    
    return frIDs;
  }, [setValue, transformSavedDataToFormValues]);

  /**
   * Extracts File objects from form data for upload
   * 
   * @returns {Array} Array of File objects ready for upload
   */
  const getFilesForUpload = useCallback(() => {
    const currentValues = getValues();
    const files = [];
    
    // Look for file fields (fields ending with '-files')
    Object.keys(currentValues).forEach(fieldName => {
      if (fieldName.endsWith('-files') && currentValues[fieldName]) {
        const fileArray = currentValues[fieldName];
        if (Array.isArray(fileArray)) {
          fileArray.forEach(file => {
            // Check if this is an actual File object (not just metadata)
            // and not a saved image that's already uploaded
            if (file instanceof File && !file._isSavedImage) {
              files.push(file);
            }
          });
        }
      }
    });
    
    return files;
  }, [getValues]);

  /**
   * Gets current form data formatted for API submission
   * Filters out empty values and structures data for backend processing
   * 
   * @param {Object} previousData - Optional previous data to compare against for change detection
   * @param {boolean} changedOnly - If true, only return changed values
   * @returns {Array} Array of question-value pairs ready for API
   */
  const getFormDataForSubmission = useCallback((previousData = null, changedOnly = false) => {
    const currentValues = getValues();
    
    
    // Convert previous data to a map for easier comparison
    const previousMap = new Map();
    if (previousData && Array.isArray(previousData)) {
      previousData.forEach(item => {
        previousMap.set(item.question, item.value);
      });
    }
    
    // Step 1: Get all fields with values (including explain and responsible fields)
    const fieldsWithValues = Object.keys(currentValues)
      .filter(key => {
        const value = currentValues[key];
        // Include non-empty values, but keep false values for checkboxes
        // Special case: for responsible fields, always include them if they exist
        // because clearing a responsible field (empty string) is still a valid change
        if (key.includes('-responsible')) {
          return value !== undefined && value !== null;
        }
        return value !== undefined && 
               value !== null && 
               value !== '' &&
               !(Array.isArray(value) && value.length === 0);
      });

    // Step 2: Filter for changed fields if requested
    let fieldsToInclude = fieldsWithValues;
    
    // If we're looking for changed fields only, we need to also check for responsible fields
    // that may have been cleared (not in fieldsWithValues but changed from previous state)
    if (changedOnly && previousData) {
      // Check for responsible fields that were cleared (changed from having a value to empty)
      const responsibleFieldsToCheck = Object.keys(currentValues).filter(key => key.includes('-responsible'));
      
      responsibleFieldsToCheck.forEach(key => {
        const currentValue = currentValues[key];
        const previousValue = previousMap.get(key);
        
        // If the field was cleared (previous had value, current is empty) include it
        if (previousValue && previousValue !== '' && (!currentValue || currentValue === '')) {
          if (!fieldsToInclude.includes(key)) {
            fieldsToInclude.push(key);
          }
        }
      });
      
      // Now filter for changed fields
      fieldsToInclude = fieldsToInclude.filter(key => {
        const currentValue = currentValues[key];
        const previousValue = previousMap.get(key);
        
        // Include if value is new or has changed
        if (previousValue === undefined) {
          return true;
        }
        
        // Convert both to strings for comparison (form values are usually strings)
        const currentStr = String(currentValue || '');
        const previousStr = String(previousValue || '');
        
        return currentStr !== previousStr;
      });
    }

    // Step 3: Convert to API format, handling explain/responsible fields
    const formattedData = [];
    const processedAreas = new Set();

    fieldsToInclude.forEach(key => {
      const value = currentValues[key];
      
      if (key.includes('-files')) {
        // Skip file fields - they're handled separately
        return;
      }
      
      if (key.includes('-explain') || key.includes('-responsible')) {
        // For explain/responsible fields, create metadata-only records
        const parts = key.split('-');
        if (parts.length >= 2) {
          const areaKey = `${parts[0]}-${parts[1]}`;
          
          // Only process each area once
          if (!processedAreas.has(areaKey)) {
            processedAreas.add(areaKey);
            
            const explainKey = `${areaKey}-explain`;
            const responsibleKey = `${areaKey}-responsible`;
            
            // Check if either explain or responsible has values
            const explainValue = currentValues[explainKey];
            const responsibleValue = currentValues[responsibleKey];
            
            // Include if explain has content OR if responsible field exists (including empty string)
            // This ensures we can clear responsible fields by sending empty values
            const hasExplain = explainValue && explainValue.trim();
            const hasResponsible = responsibleValue !== undefined && responsibleValue !== null;
            
            if (hasExplain || hasResponsible) {
              formattedData.push({
                question: areaKey,
                value: '', // Empty value for metadata-only records
                explain: explainValue || '',
                responsible: responsibleValue || ''
              });
            }
          }
        }
        return;
      }
      
      // For regular question fields, include with their associated explain/responsible
      const parts = key.split('-');
      if (parts.length >= 2) {
        const areaKey = `${parts[0]}-${parts[1]}`;
        const explainKey = `${areaKey}-explain`;
        const responsibleKey = `${areaKey}-responsible`;
        
        formattedData.push({
          question: key,
          value: value,
          explain: currentValues[explainKey] || '',
          responsible: currentValues[responsibleKey] || ''
        });
        
        // Mark this area as processed
        processedAreas.add(areaKey);
      } else {
        // Summary fields and other fields without area structure
        formattedData.push({
          question: key,
          value: value
        });
      }
    });



    return formattedData;
  }, [getValues]);

  /**
   * Validates if all required fields in the survey are completed
   * This is used before final submission to ensure completeness
   * 
   * @returns {boolean} True if survey is complete, false otherwise
   */
  const validateSurveyCompletion = useCallback(() => {
    const currentValues = getValues();
    const errors = formMethods.formState.errors;
    
    // Check if there are any validation errors
    if (Object.keys(errors).length > 0) {
      return false;
    }

    // Check that strengths and opportunities are filled
    if (!currentValues.strengths || !currentValues.opportunities) {
      return false;
    }

    // Check for required conditional fields when answers are "no"
    const fieldNames = Object.keys(currentValues);

    // Group fields by section-area to check conditional requirements
    const sectionAreas = new Set();
    const noAnswers = new Set();
    
    // Find all section-area combinations and identify "no" answers
    fieldNames.forEach(fieldName => {
      const parts = fieldName.split('-');
      if (parts.length >= 3 && !fieldName.includes('-explain') && 
          !fieldName.includes('-responsible') && !fieldName.includes('-files')) {
        const sectionArea = `${parts[0]}-${parts[1]}`;
        sectionAreas.add(sectionArea);
        
        // Check if this is a "no" answer (0)
        if (String(currentValues[fieldName]) === '0') {
          noAnswers.add(sectionArea);
        }
      }
    });

    // For each section-area with "no" answers, check required fields
    for (const sectionArea of noAnswers) {
      const explainField = `${sectionArea}-explain`;
      const responsibleField = `${sectionArea}-responsible`;
      
      // Check if explanation is provided and meets minimum requirements
      const explanation = currentValues[explainField];
      if (!explanation || explanation.trim().length < 10) {
        return false;
      }
      
      // Check if responsibility is assigned
      const responsible = currentValues[responsibleField];
      if (!responsible || responsible === 'Please select') {
        return false;
      }
    }

    return true;
  }, [getValues, formMethods.formState.errors]);

  /**
   * Validates a specific section for completeness
   * This is used when changing tabs to ensure required fields are filled
   * 
   * @param {number} sectionNumber - Section number (1-based)
   * @param {Object} surveyInfo - Survey structure information (optional)
   * @returns {Object} Object with isValid boolean and missingItems array
   */
  const validateSection = useCallback((sectionNumber, surveyInfo = null) => {
    const currentValues = getValues();
    const missingItems = [];
    
    // Get all fields for this section
    const sectionFields = Object.keys(currentValues).filter(fieldName => {
      const parts = fieldName.split('-');
      return parts.length >= 2 && parts[0] === String(sectionNumber);
    });
    
    // Group by section-area
    const sectionAreas = new Set();
    const noAnswers = new Set();
    
    // Find all section-area combinations and identify "no" answers for this section
    sectionFields.forEach(fieldName => {
      const parts = fieldName.split('-');
      if (parts.length >= 3 && !fieldName.includes('-explain') && 
          !fieldName.includes('-responsible') && !fieldName.includes('-files')) {
        const sectionArea = `${parts[0]}-${parts[1]}`;
        sectionAreas.add(sectionArea);
        
        // Check if this is a "no" answer (0)
        if (String(currentValues[fieldName]) === '0') {
          noAnswers.add(sectionArea);
        }
      }
    });

    // Helper function to get section and area names from survey structure
    const getSectionAndAreaNames = (sectionNum, areaNum) => {
      if (!surveyInfo) {
        return { sectionName: `Section ${sectionNum}`, areaName: `Area ${areaNum}` };
      }

      // Get section name from surveyInfo keys
      const sectionKeys = Object.keys(surveyInfo);
      const sectionName = sectionKeys[sectionNum - 1] || `Section ${sectionNum}`;

      // Find area name by looking through the questions in this section
      let areaName = `Area ${areaNum}`;
      const sectionData = surveyInfo[sectionName];
      
      if (sectionData && Array.isArray(sectionData)) {
        // Find a question that matches our area number to get the area name
        const matchingQuestion = sectionData.find(question => {
          // Questions are grouped by area, so we need to find area index
          // This is a bit complex since we need to determine which area index corresponds to our areaNum
          return question.area; // For now, we'll look for any question with an area property
        });

        if (matchingQuestion) {
          // Group questions by area to find the correct area name
          const areaGroups = {};
          sectionData.forEach(question => {
            if (!areaGroups[question.area]) {
              areaGroups[question.area] = [];
            }
            areaGroups[question.area].push(question);
          });
          
          // Get the area name by index
          const areaKeys = Object.keys(areaGroups);
          areaName = areaKeys[areaNum - 1] || `Area ${areaNum}`;
        }
      }

      return { sectionName, areaName };
    };

    // For each section-area with "no" answers, check required fields
    for (const sectionArea of noAnswers) {
      const [section, area] = sectionArea.split('-');
      const { sectionName, areaName } = getSectionAndAreaNames(parseInt(section), parseInt(area));
      
      const explainField = `${sectionArea}-explain`;
      const responsibleField = `${sectionArea}-responsible`;
      
      // Check if explanation is provided and meets minimum requirements
      const explanation = currentValues[explainField];
      if (!explanation || explanation.trim().length < 10) {
        missingItems.push(`${sectionName}, ${areaName}: Explanation required (minimum 10 characters)`);
      }
      
      // Check if responsibility is assigned
      const responsible = currentValues[responsibleField];
      if (!responsible || responsible === '' || responsible === 'Please select') {
        missingItems.push(`${sectionName}, ${areaName}: Responsibility assignment required`);
      }
    }

    return {
      isValid: missingItems.length === 0,
      missingItems: missingItems
    };
  }, [getValues]);

  /**
   * Gets detailed information about what's missing from the survey
   * This is used to show users specific items they need to complete
   * 
   * @returns {Object} Object with isComplete boolean and missingItems array
   */
  const getSurveyCompletionDetails = useCallback(() => {
    const currentValues = getValues();
    const errors = formMethods.formState.errors;
    const missingItems = [];
    
    // Check if there are any validation errors
    if (Object.keys(errors).length > 0) {
      Object.keys(errors).forEach(fieldName => {
        const error = errors[fieldName];
        if (error && error.message) {
          missingItems.push(`Field validation error: ${error.message}`);
        }
      });
    }

    // Check that strengths and opportunities are filled
    if (!currentValues.strengths) {
      missingItems.push('Strengths summary must be completed');
    }
    if (!currentValues.opportunities) {
      missingItems.push('Opportunities for improvement summary must be completed');
    }

    // Check for required conditional fields when answers are "no"
    const fieldNames = Object.keys(currentValues);

    // Group fields by section-area to check conditional requirements
    const sectionAreas = new Set();
    const noAnswers = new Set();
    
    // Find all section-area combinations and identify "no" answers
    fieldNames.forEach(fieldName => {
      const parts = fieldName.split('-');
      if (parts.length >= 3 && !fieldName.includes('-explain') && 
          !fieldName.includes('-responsible') && !fieldName.includes('-files')) {
        const sectionArea = `${parts[0]}-${parts[1]}`;
        sectionAreas.add(sectionArea);
        
        // Check if this is a "no" answer (0)
        if (String(currentValues[fieldName]) === '0') {
          noAnswers.add(sectionArea);
        }
      }
    });

    // For each section-area with "no" answers, check required fields
    for (const sectionArea of noAnswers) {
      const explainField = `${sectionArea}-explain`;
      const responsibleField = `${sectionArea}-responsible`;
      const [section, area] = sectionArea.split('-');
      
      // Check if explanation is provided and meets minimum requirements
      const explanation = currentValues[explainField];
      if (!explanation || explanation.trim().length < 10) {
        missingItems.push(`Section ${section}, Area ${area}: Explanation required (minimum 10 characters)`);
      }
      
      // Check if responsibility is assigned
      const responsible = currentValues[responsibleField];
      if (!responsible || responsible === 'Please select') {
        missingItems.push(`Section ${section}, Area ${area}: Responsibility assignment required`);
      }
    }

    return {
      isComplete: missingItems.length === 0,
      missingItems: missingItems
    };
  }, [getValues, formMethods.formState.errors]);

  /**
   * Marks the form as having unsaved changes
   * This is typically called when form values change
   */
  const markFormAsDirty = useCallback(() => {
    setHasUnsavedChanges(true);
  }, []);

  /**
   * Marks the form as clean (no unsaved changes)
   * This is typically called after successful save
   */
  const markFormAsClean = useCallback(() => {
    setHasUnsavedChanges(false);
  }, []);

  /**
   * Resets the form to its initial state
   * Used when starting a new survey
   */
  const resetForm = useCallback(() => {
    reset({}, { keepDefaultValues: true });
    setHasUnsavedChanges(false);
  }, [reset]);

  return {
    // React Hook Form methods
    control,
    handleSubmit,
    getValues,
    setValue,
    watch,
    reset,
    errors,
    isDirty,
    
    // Custom form state
    hasUnsavedChanges,
    
    // Custom methods
    loadSavedData,
    mergeSavedData,
    getFormDataForSubmission,
    getFilesForUpload,
    validateSurveyCompletion,
    validateSection,
    getSurveyCompletionDetails,
    markFormAsDirty,
    markFormAsClean,
    resetForm,
    
    // Expose all form methods for advanced usage
    formMethods,
  };
};